import React, { useState, useEffect } from 'react';
import {
  FileText, Save, Eye, EyeOff, Copy, Info, ChevronDown, ChevronRight,
  Sparkles, Trash2, Edit, Settings, Bell, Zap, Plus, Check,
  AlertTriangle, ChevronUp, Globe, Tag, X, FileEdit, CircleDashed
} from 'lucide-react';
import api from '../services/api';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import { useLoader } from '../contexts/LoaderContext';

// ── Helpers ────────────────────────────────────────────────────────────────
const inputFocus = (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.1)'; };
const inputBlur  = (e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; };
const cls        = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none transition-all text-sm font-medium";



const MODELE_DEFAUT = `ARTICLE 1 - OBJET DU CONTRAT

Le présent contrat a pour objet de définir les conditions d'emploi du Salarié au sein de l'entreprise {{entreprise_nom}}.
Le salarié est engagé en qualité de {{poste}} au sein du département {{departement}}.


ARTICLE 2 - NATURE ET DURÉE DU CONTRAT

Le présent contrat est un {{type_contrat}} prenant effet le {{date_debut_contrat}}.
Date de fin : {{date_fin_contrat}}
Période d'essai jusqu'au : {{periode_essai}}


ARTICLE 3 - FONCTIONS ET MISSIONS

Le salarié exercera les fonctions de {{poste}}.

Ses missions principales seront notamment :
- Exécuter les tâches confiées dans le cadre de ses fonctions
- Respecter les directives et instructions de sa hiérarchie
- Participer activement à la vie de l'entreprise
- Se conformer au règlement intérieur de l'entreprise


ARTICLE 4 - LIEU DE TRAVAIL

Le salarié exercera ses fonctions au siège social de l'entreprise :
{{entreprise_adresse}}, {{entreprise_ville}}

Le salarié pourra être amené à se déplacer ponctuellement selon les besoins de l'entreprise.


ARTICLE 5 - CONFIDENTIALITÉ

Le salarié s'engage à observer la plus stricte confidentialité sur toutes les informations
dont il aura connaissance dans l'exercice de ses fonctions.
Cette obligation subsistera après la rupture du contrat.


ARTICLE 6 - DISPOSITIONS GÉNÉRALES

Le présent contrat est régi par la législation du travail en vigueur.
Toute modification devra faire l'objet d'un avenant signé par les deux parties.`;



// ── Panneau variables ──────────────────────────────────────────────────────
const VARIABLES = [
  {
    cat: 'Employé', items: [
      { v: '{{nom}}',                d: 'Nom de l\'employé' },
      { v: '{{prenom}}',             d: 'Prénom de l\'employé' },
      { v: '{{date_naissance}}',     d: 'Date de naissance' },
      { v: '{{date_embauche}}',      d: 'Date d\'embauche' },
      { v: '{{date_debut_contrat}}', d: 'Date de début du contrat' },
      { v: '{{date_fin_contrat}}',   d: 'Date de fin (durée déterminée)' },
      { v: '{{periode_essai}}',      d: 'Fin de période d\'essai' },
      { v: '{{type_contrat}}',       d: 'Type de contrat' },
      { v: '{{poste}}',              d: 'Poste occupé' },
      { v: '{{departement}}',        d: 'Département' },
      { v: '{{salaire}}',            d: 'Salaire mensuel' },
      { v: '{{adresse_employe}}',    d: 'Adresse de l\'employé' },
      { v: '{{email}}',              d: 'Email de l\'employé' },
      { v: '{{telephone}}',          d: 'Téléphone de l\'employé' },
      { v: '{{mode_paiement}}',      d: 'Mode de paiement du salaire' },
    ]
  },
  {
    cat: 'Entreprise', items: [
      { v: '{{entreprise_nom}}',        d: 'Nom de l\'entreprise' },
      { v: '{{entreprise_adresse}}',    d: 'Adresse de l\'entreprise' },
      { v: '{{entreprise_ville}}',      d: 'Ville de l\'entreprise' },
      { v: '{{entreprise_telephone}}',  d: 'Téléphone de l\'entreprise' },
      { v: '{{entreprise_email}}',      d: 'Email de l\'entreprise' },
      { v: '{{signataire_nom}}',        d: 'Nom du signataire' },
      { v: '{{signataire_fonction}}',   d: 'Fonction du signataire' },
    ]
  },
  { cat: 'Système', items: [{ v: '{{date_generation}}', d: 'Date de génération du PDF' }] }
];

// ── Éditeur de modèle ──────────────────────────────────────────────────────
const Editeur = ({ value, onChange, onSave, saving, onInsert, activeTarget, targetId, targetLabel }) => {
  const [preview, setPreview] = useState(false);
  const isActive = activeTarget === targetId;

  return (
    <div className={`rounded-xl border-2 transition-all ${isActive ? 'border-[#448696] shadow-md' : 'border-gray-200'}`}
      onClick={() => onInsert && onInsert(null, targetId)} // activer cible insertion
    >
      {/* Barre d'outils */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl flex-wrap">
        <div className="flex items-center gap-2 flex-1">
          {isActive && <div className="w-2 h-2 rounded-full bg-[#448696] animate-pulse" />}
          <span className="text-xs font-bold text-gray-600">
            {targetLabel}
          </span>
          {isActive && <span className="text-xs text-[#448696] font-semibold">— Éditeur actif</span>}
        </div>
        <div className="flex items-center gap-2">
          {!value?.trim() && (
            <button onClick={(e) => { e.stopPropagation(); onChange(MODELE_DEFAUT); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
              style={{ borderColor: '#fed774', color: '#92650a', backgroundColor: '#fffbeb' }}>
              <Sparkles size={13} /> Modèle par défaut
            </button>
          )}
          {value?.trim() && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onChange(MODELE_DEFAUT); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                style={{ borderColor: '#fed774', color: '#92650a', backgroundColor: '#fffbeb' }}>
                <Sparkles size={13} /> Réinitialiser
              </button>
              <button onClick={(e) => { e.stopPropagation(); onChange(''); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-all">
                <Trash2 size={13} /> Vider
              </button>
            </>
          )}
          <button onClick={(e) => { e.stopPropagation(); setPreview(p => !p); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
            style={{ backgroundColor: preview ? '#448696' : '#f3f4f6', color: preview ? 'white' : '#374151' }}>
            {preview ? <EyeOff size={13} /> : <Eye size={13} />}
            {preview ? 'Masquer' : 'Aperçu'}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onSave(); }} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#448696' }}>
            <Save size={13} /> {saving ? '...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Textarea ou aperçu */}
      {preview ? (
        <div className="p-5 bg-white rounded-b-xl whitespace-pre-wrap text-xs leading-relaxed text-gray-700 max-h-80 overflow-y-auto"
          style={{ fontFamily: 'Courier New, monospace' }}>
          {value || 'Aucun contenu à afficher...'}
        </div>
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder={`Cliquez sur "Modèle par défaut" pour démarrer avec un contrat pré-rédigé,\nou écrivez votre modèle ici en utilisant les variables disponibles à droite.`}
          rows={18}
          className="w-full px-4 py-3 bg-white rounded-b-xl focus:outline-none text-xs resize-none"
          style={{ fontFamily: 'Courier New, monospace', lineHeight: 1.7 }}
        />
      )}
    </div>
  );
};

// ── Composant principal ────────────────────────────────────────────────────
const ModeleContrat = () => {
  const { showLoader, hideLoader } = useLoader();

  // Config globale
  const [modeleGlobal, setModeleGlobal]         = useState('');
  const [signataire, setSignataire]             = useState({ nom: '', fonction: '' });
  const [alerteJours, setAlerteJours]           = useState(30);
  const [modeDemarrage, setModeDemarrage]       = useState('manuel');
  const [savingConfig, setSavingConfig]         = useState(false);

  // Types de contrats
  const [contractTypes, setContractTypes]       = useState([]);
  const [typeModeles, setTypeModeles]           = useState({});
  const [newType, setNewType]                   = useState({ nom: '', description: '', has_end_date: false });
  const [editingType, setEditingType]           = useState(null);
  const [savingType, setSavingType]             = useState(false);
  const [savingTypeId, setSavingTypeId]         = useState(null);
  const [expandedId, setExpandedId]             = useState(null);
  const [deleteConfirm, setDeleteConfirm]       = useState(null);

  // UI
  const [activeTarget, setActiveTarget]         = useState('global');
  const [loading, setLoading]                   = useState(true);
  const [toast, setToast]                       = useState(null);
  const [expandedVarCat, setExpandedVarCat]     = useState({});

  const ok  = (m) => setToast({ message: m, type: 'success' });
  const err = (m) => setToast({ message: m, type: 'error' });

  // ── Chargement ─────────────────────────────────────────────────────────
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      showLoader(); setLoading(true);
      const [cr, ctr] = await Promise.all([api.parametreContrat.get(), api.contractTypes.getAll()]);
      if (cr.success) {
        setModeleGlobal(cr.data.modele_contrat || '');
        setSignataire({ nom: cr.data.signataire_nom || '', fonction: cr.data.signataire_fonction || '' });
        setAlerteJours(cr.data.alerte_expiration_jours || 30);
        setModeDemarrage(cr.data.mode_demarrage_contrat || 'manuel');
      }
      if (ctr.success) {
        setContractTypes(ctr.data);
        const m = {}; ctr.data.forEach(ct => { m[ct.id] = ct.modele_contrat || ''; });
        setTypeModeles(m);
      }
    } catch { err('Erreur lors du chargement'); }
    finally { setLoading(false); hideLoader(); }
  };

  // ── CRUD Types ─────────────────────────────────────────────────────────
  const createType = async () => {
    if (!newType.nom.trim()) return;
    setSavingType(true);
    try {
      const r = await api.contractTypes.create(newType);
      if (r.success) { setNewType({ nom: '', description: '', has_end_date: false }); await loadAll(); ok('Type créé'); }
      else err(r.message);
    } catch { err('Erreur création'); } finally { setSavingType(false); }
  };

  const updateType = async () => {
    if (!editingType?.nom.trim()) return;
    setSavingType(true);
    try {
      const r = await api.contractTypes.update(editingType.id, { nom: editingType.nom, description: editingType.description, has_end_date: editingType.has_end_date, modele_contrat: typeModeles[editingType.id] || null });
      if (r.success) { setEditingType(null); await loadAll(); ok('Type mis à jour'); }
      else err(r.message);
    } catch { err('Erreur mise à jour'); } finally { setSavingType(false); }
  };

  const deleteType = async (id) => {
    try {
      const r = await api.contractTypes.delete(id);
      if (r.success) { setDeleteConfirm(null); await loadAll(); ok('Type supprimé'); }
      else err(r.message);
    } catch { err('Erreur suppression'); }
  };

  const saveTypeModele = async (id) => {
    setSavingTypeId(id);
    try {
      const type = contractTypes.find(ct => ct.id === id);
      const r = await api.contractTypes.update(id, { nom: type.nom, description: type.description, has_end_date: type.has_end_date, modele_contrat: typeModeles[id] || null });
      if (r.success) ok(`Modèle "${type.nom}" enregistré`);
      else err(r.message);
    } catch { err('Erreur sauvegarde modèle'); } finally { setSavingTypeId(null); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await api.parametreContrat.update({ modele_contrat: modeleGlobal, signataire_nom: signataire.nom, signataire_fonction: signataire.fonction, alerte_expiration_jours: alerteJours, mode_demarrage_contrat: modeDemarrage });
      if (r.success) ok('Configuration enregistrée');
      else err(r.message);
    } catch { err('Erreur sauvegarde'); } finally { setSavingConfig(false); }
  };

  // ── Insertion variable ─────────────────────────────────────────────────
  const insertVar = (v, targetOverride) => {
    const target = targetOverride || activeTarget;
    if (!v) return; // juste activer la cible
    if (target === 'global') setModeleGlobal(p => p + v);
    else setTypeModeles(p => ({ ...p, [target]: (p[target] || '') + v }));
  };

  const setTarget = (id) => setActiveTarget(id);

  if (loading) return null;

  return (
    <div className="space-y-8">

      {/* ══════════════════════════════════════════════════════════════
          SECTION 1 — EN-TÊTE FLUX UTILISATEUR
      ══════════════════════════════════════════════════════════════ */}
    

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">

          {/* ══════════════════════════════════════════════════════════
              SECTION 2 — TYPES DE CONTRATS
          ══════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ backgroundColor: '#fafcfe' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: '#448696' }}>1</div>
              <div>
                <p className="text-sm font-bold text-gray-900">Types de contrats</p>
                <p className="text-xs text-gray-400">Créez et gérez vos types de contrats</p>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Formulaire création */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nouveau type</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input type="text" value={newType.nom}
                    onChange={e => setNewType(p => ({ ...p, nom: e.target.value }))}
                    placeholder="Nom du type (ex: CDI)" className={cls}
                    onFocus={inputFocus} onBlur={inputBlur}
                    onKeyPress={e => { if (e.key === 'Enter') createType(); }} />
                  <input type="text" value={newType.description}
                    onChange={e => setNewType(p => ({ ...p, description: e.target.value }))}
                    placeholder="Description (optionnel)" className={cls}
                    onFocus={inputFocus} onBlur={inputBlur} />
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 px-3 py-2.5 bg-white border border-gray-200 rounded-lg cursor-pointer"
                      onClick={() => setNewType(p => ({ ...p, has_end_date: !p.has_end_date }))}>
                      <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all ${newType.has_end_date ? 'bg-[#448696]' : 'border-2 border-gray-300'}`}>
                        {newType.has_end_date && <Check size={11} className="text-white" />}
                      </div>
                      <span className="text-xs font-semibold text-gray-700">Durée déterminée</span>
                    </div>
                    <button onClick={createType} disabled={!newType.nom.trim() || savingType}
                      className="px-4 py-2.5 text-white rounded-lg font-bold text-sm disabled:opacity-40 hover:opacity-90 flex items-center gap-1.5 whitespace-nowrap"
                      style={{ backgroundColor: '#448696' }}>
                      <Plus size={15} /> Ajouter
                    </button>
                  </div>
                </div>
              </div>

              {/* Tableau des types */}
              {contractTypes.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
                  <Tag size={32} className="mx-auto mb-3 text-gray-300" />
                  Aucun type de contrat - créez votre premier type ci-dessus
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  {/* En-têtes colonnes */}
                  <div className="grid grid-cols-12 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <div className="col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Type de contrat</div>
                    <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Durée</div>
                    <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Modèle</div>
                    <div className="col-span-2 text-xs font-bold text-gray-500 uppercase tracking-wide text-right">Actions</div>
                  </div>

                  {/* Lignes */}
                  {contractTypes.map((type, idx) => (
                    <div key={type.id} className={idx < contractTypes.length - 1 || expandedId === type.id ? 'border-b border-gray-100' : ''}>

                      {/* Ligne principale */}
                      <div className={`grid grid-cols-12 items-center px-4 py-3 transition-colors ${expandedId === type.id ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>

                        {/* Col 1 — Nom */}
                        <div className="col-span-4">
                          {editingType?.id === type.id ? (
                            <input type="text" value={editingType.nom}
                              onChange={e => setEditingType(p => ({ ...p, nom: e.target.value }))}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-medium focus:outline-none"
                              onFocus={inputFocus} onBlur={inputBlur} />
                          ) : (
                            <div>
                              <p className="text-sm font-bold text-gray-900">{type.nom}</p>
                              {type.description && <p className="text-xs text-gray-400 mt-0.5">{type.description}</p>}
                              <p className="text-xs text-gray-400 mt-0.5">{type.nb_employes} employé{type.nb_employes > 1 ? 's' : ''}</p>
                            </div>
                          )}
                        </div>

                        {/* Col 2 — Durée */}
                        <div className="col-span-3">
                          {editingType?.id === type.id ? (
                            <div className="flex items-center gap-2 cursor-pointer"
                              onClick={() => setEditingType(p => ({ ...p, has_end_date: !p.has_end_date }))}>
                              <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${editingType.has_end_date ? 'bg-[#448696]' : 'border-2 border-gray-300'}`}>
                                {editingType.has_end_date && <Check size={11} className="text-white" />}
                              </div>
                              <span className="text-xs font-semibold text-gray-600">Déterminée</span>
                            </div>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${type.has_end_date ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-700'}`}>
                              {type.has_end_date ? 'Déterminée' : 'Indéterminée'}
                            </span>
                          )}
                        </div>

                        {/* Col 3 — Statut modèle */}
                        <div className="col-span-3">
                          {typeModeles[type.id]?.trim() ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700">
                              <Check size={11} /> Modèle spécifique
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                              Utilise le modèle global
                            </span>
                          )}
                        </div>

                        {/* Col 4 — Actions */}
                        <div className="col-span-2 flex items-center justify-end gap-1.5">
                          {editingType?.id === type.id ? (
                            <>
                              <button onClick={updateType} disabled={savingType}
                                className="px-2.5 py-1.5 text-white rounded-lg text-xs font-bold hover:opacity-90"
                                style={{ backgroundColor: '#448696' }}>✓</button>
                              <button onClick={() => setEditingType(null)}
                                className="px-2.5 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-300">✗</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => { setExpandedId(expandedId === type.id ? null : type.id); setTarget(expandedId === type.id ? 'global' : type.id); }}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all"
                                style={{ backgroundColor: expandedId === type.id ? '#448696' : '#f0f9fb', color: expandedId === type.id ? 'white' : '#448696' }}>
                                <FileText size={12} />
                                {expandedId === type.id ? 'Fermer' : 'Modèle'}
                              </button>
                              <button onClick={() => setEditingType({ ...type })}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-all">
                                <Edit size={13} />
                              </button>
                              <button onClick={() => setDeleteConfirm(type.id)}
                                disabled={type.nb_employes > 0}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Éditeur de modèle du type */}
                      {expandedId === type.id && (
                        <div className="p-5 border-t border-teal-100 bg-teal-50/30">
                          <Editeur
                            value={typeModeles[type.id] || ''}
                            onChange={(val) => setTypeModeles(p => ({ ...p, [type.id]: val }))}
                            onSave={() => saveTypeModele(type.id)}
                            saving={savingTypeId === type.id}
                            onInsert={(v) => { if (v) insertVar(v, type.id); setTarget(type.id); }}
                            activeTarget={activeTarget}
                            targetId={type.id}
                            targetLabel={`Modèle spécifique - ${type.nom}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              SECTION 3 — MODÈLE GLOBAL
          ══════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ backgroundColor: '#fafcfe' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: '#6366f1' }}>2</div>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-900">Modèle global</p>
                <p className="text-xs text-gray-400">Utilisé si le type de contrat n'a pas de modèle spécifique</p>
              </div>
              <div className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600 border border-indigo-100">
                Fallback
              </div>
            </div>
            <div className="p-6" onClick={() => setTarget('global')}>

          <div className="mb-4 p-4 rounded-xl border border-blue-200 bg-blue-50 space-y-2">
              <p className="font-bold text-sm text-blue-800">Comment fonctionne le modèle de contrat ?</p>
              <p className="text-xs text-blue-700">Le PDF généré contient <strong>toujours automatiquement</strong> :</p>
              <ul className="text-xs text-blue-700 list-disc ml-4 space-y-0.5">
                <li>Les <strong>informations de l'employeur et du salarié</strong> - récupérées automatiquement</li>
                <li>L'<strong>article Rémunération</strong> - avec le salaire et mode de paiement de l'employé</li>
                <li>Les <strong>dates du contrat</strong> - début, fin, période d'essai si applicable</li>
              </ul>
              <p className="text-xs text-blue-700 mt-1 font-medium">
                Votre modèle doit contenir <strong>uniquement les articles de contenu</strong> (objet, fonctions, lieu de travail, confidentialité...).
                Ne réécrivez pas les parties ci-dessus - elles seraient dupliquées dans le PDF.
              </p>
            </div>


              <Editeur
                value={modeleGlobal}
                onChange={setModeleGlobal}
                onSave={saveConfig}
                saving={savingConfig}
                onInsert={(v) => { if (v) insertVar(v, 'global'); setTarget('global'); }}
                activeTarget={activeTarget}
                targetId="global"
                targetLabel="Modèle global"
              />
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              SECTION 4 — CONFIGURATION RH
          ══════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ backgroundColor: '#fafcfe' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: '#f59e0b' }}>3</div>
              <div>
                <p className="text-sm font-bold text-gray-900">Configuration RH</p>
                <p className="text-xs text-gray-400">Signataire, alertes et mode de démarrage de contrat</p>
              </div>
            </div>
            <div className="p-6 space-y-6">

              {/* Signataire */}
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Signataire du contrat</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1.5">Nom</label>
                    <input type="text" value={signataire.nom}
                      onChange={e => setSignataire(p => ({ ...p, nom: e.target.value }))}
                      placeholder="Ex: Jean Dupont" className={cls} onFocus={inputFocus} onBlur={inputBlur} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1.5">Fonction</label>
                    <input type="text" value={signataire.fonction}
                      onChange={e => setSignataire(p => ({ ...p, fonction: e.target.value }))}
                      placeholder="Ex: Directeur Général" className={cls} onFocus={inputFocus} onBlur={inputBlur} />
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* Alerte */}
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <Bell size={11} /> Alerte expiration contrat
                </p>
                <div className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                  <input type="number" min="1" max="365" value={alerteJours}
                    onChange={e => setAlerteJours(parseInt(e.target.value) || 30)}
                    className="w-20 px-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none font-black text-sm text-center"
                    onFocus={inputFocus} onBlur={inputBlur} />
                  <div>
                    <p className="text-sm font-bold text-gray-800">jours avant expiration</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Badge <strong>"Expire bientôt"</strong> déclenché à <strong>{alerteJours}j</strong> avant la date de fin du contrat
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* Mode démarrage */}
             <div>
  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
   Démarrage contrat après période d'essai
  </p>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {[
      {
        v: 'manuel',
        label: 'Manuel',
        icon: CircleDashed,
        desc: "L'agent RH doit confirmer manuellement. Badge orange d'alerte dans la liste des employés."
      },
      {
        v: 'automatique',
        label: 'Automatique',
        icon: Sparkles,
        desc: "Le contrat démarre automatiquement dès la fin de la période d'essai."
      }
    ].map(({ v, label, icon: Icon, desc }) => (

      <button
        key={v}
        type="button"
        onClick={() => setModeDemarrage(v)}
        className="p-4 rounded-xl border-2 text-left transition-all"
        style={{
          borderColor: modeDemarrage === v ? '#448696' : '#e5e7eb',
          backgroundColor: modeDemarrage === v ? '#f0f9fb' : 'white'
        }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          
          <Icon
            size={16}
            className={modeDemarrage === v ? "text-[#448696]" : "text-gray-400"}
          />

          <span
            className="text-sm font-bold"
            style={{ color: modeDemarrage === v ? '#448696' : '#374151' }}
          >
            {label}
          </span>

          {modeDemarrage === v && (
            <span
              className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: '#448696' }}
            >
              Actif
            </span>
          )}
        </div>

        <p className="text-xs text-gray-500 leading-relaxed">
          {desc}
        </p>
      </button>

    ))}
  </div>
</div>

              <div className="border-t border-gray-100 pt-4 flex justify-end">
                <button onClick={saveConfig} disabled={savingConfig}
                  className="flex items-center gap-2 px-7 py-3 rounded-xl font-bold text-sm text-white shadow-md hover:shadow-lg hover:opacity-95 transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#448696' }}>
                  <Save size={16} /> {savingConfig ? 'Enregistrement...' : 'Enregistrer la configuration'}
                </button>
              </div>

            </div>
          </div>

        </div>

        {/* ── COLONNE VARIABLES ───────────────────────────────────── */}
        <div>
          <div className="sticky top-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100" style={{ backgroundColor: '#fafcfe' }}>
                <p className="text-sm font-bold text-gray-900">Variables disponibles</p>
                <p className="text-xs text-gray-400 mt-0.5">Cliquez pour insérer dans l'éditeur actif</p>
              </div>

         

              <div className="p-4 space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                {VARIABLES.map((group) => (
                  <div key={group.cat}>
                    <button onClick={() => setExpandedVarCat(p => ({ ...p, [group.cat]: !p[group.cat] }))}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="text-xs font-bold text-gray-700">{group.cat}</span>
                      {expandedVarCat[group.cat] ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </button>
                    {expandedVarCat[group.cat] && (
                      <div className="space-y-1 mt-1">
                        {group.items.map((item) => (
                          <div key={item.v}
                            className="group flex items-start gap-2 p-2 bg-gray-50 rounded-lg hover:bg-teal-50 cursor-pointer transition-colors"
                            onClick={() => insertVar(item.v)}>
                            <code className="text-xs font-mono font-bold flex-shrink-0 mt-0.5" style={{ color: '#448696' }}>{item.v}</code>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 leading-tight">{item.d}</p>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.v); }}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all flex-shrink-0">
                              <Copy size={11} className="text-gray-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500 text-center font-medium"
                style={{ backgroundColor: '#fffbeb' }}>
                💡 Cliquez dans un éditeur pour l'activer, puis cliquez sur une variable
              </div>
            </div>
          </div>
        </div>

      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteType(deleteConfirm)}
        title="Supprimer ce type de contrat ?"
        message="Cette action est irréversible."
        confirmText="Supprimer" cancelText="Annuler" type="danger"
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ModeleContrat;